import { useState } from "react";
import { askQuestion } from "./ask.service";
import { AskResponse, AskResponseKind } from "../types/ask";
import { useStoreId } from "../../../core/auth/useLoggedInUser";

export type ChatMessage = { id: string } & (
  | { role: "user"; text: string }
  | { role: "assistant"; response: AskResponse }
  | { role: "error"; text: string }
  | { role: "too_large" }
  | { role: "timeout" }
  | { role: "empty" }
);

type AskContext = { selectedArticles?: any[] };

type UseAskResult = {
  ask: (question: string, context?: AskContext) => Promise<void>;
  messages: ChatMessage[];
  loading: boolean;
  clearHistory: () => void;
  appendAssistantSummary: (summary: string) => void;
  appendErrorMessage: (text: string) => void;
};

export function useAsk(): UseAskResult {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [loading, setLoading] = useState(false);
  const storeId = useStoreId();

  const appendAssistantSummary = (summary: string) => {
    const assistantMessage: ChatMessage = {
      id: `${Date.now()}-assistant-local`,
      role: "assistant",
      response: {
        intent: "task_action",
        sql: "",
        data: [],
        summary,
        tasks: null,
      },
    };

    setMessages((prev) => {
      const updated = [...prev, assistantMessage];
      return updated;
    });
  };

  const appendErrorMessage = (text: string) => {
    const errorMessage: ChatMessage = {
      id: `${Date.now()}-error-local`,
      role: "error",
      text,
    };

    setMessages((prev) => {
      const updated = [...prev, errorMessage];
      return updated;
    });
  };

  const ask = async (question: string, context?: AskContext) => {
    const userMessage: ChatMessage = {
      id: `${Date.now()}-user`,
      role: "user",
      text: question,
    };
    const nextMessages = [...messages, userMessage];
    setMessages(nextMessages);

    setLoading(true);
    try {
      const response = await askQuestion({
        question,
        storeId,
        selected_articles: context?.selectedArticles,
      });
      const newMessage: ChatMessage = response.kind
        ? {
            id: `${Date.now()}-${response.kind}`,
            role: response.kind as AskResponseKind,
          }
        : response.error
          ? { id: `${Date.now()}-error`, role: "error", text: response.error }
          : { id: `${Date.now()}-assistant`, role: "assistant", response };
      const updated = [...nextMessages, newMessage];
      setMessages(updated);
    } catch (err: any) {
      const isTimeout = err.name === "AbortError";
      const newMsg: ChatMessage = isTimeout
        ? { id: `${Date.now()}-timeout`, role: "timeout" }
        : {
            id: `${Date.now()}-error`,
            role: "error",
            text: err.message || "Something went wrong",
          };
      const updated = [...nextMessages, newMsg];
      setMessages(updated);
    } finally {
      setLoading(false);
    }
  };

  const clearHistory = () => {
    setMessages([]);
  };

  return {
    ask,
    messages,
    loading,
    clearHistory,
    appendAssistantSummary,
    appendErrorMessage,
  };
}
